/**
 * The shard shop (see docs/meteor-shop-plan.md): items bought with meteor
 * shards. Tools raise the damage a click does to a meteor; the nights add to
 * the offline rate alongside the egg-shop offline upgrades. Each tier
 * requires the one before it, so the tab shows one tile per line at a time.
 */

export type ShopEffect =
  /** Damage per click on a meteor becomes this. */
  | { kind: 'damage'; damage: number }
  /** Adds this fraction to the offline rate (0.1 = ten percentage points). */
  | { kind: 'offlineRate'; add: number }
  /** Puts one of this item in the inventory; the tile stays for sale. */
  | { kind: 'grantItem'; itemId: string }
  /** Meteors fall this many times as often. */
  | { kind: 'meteorRate'; mult: number };

export interface ShopItemDef {
  id: string;
  name: string;
  description: string;
  /** Price in shards; for a repeatable tile, the price of the first one. */
  price: number;
  effect: ShopEffect;
  /** Id of the item that must be owned first, if any. */
  requires?: string;
  /**
   * A repeatable tile can be bought over and over; each purchase costs
   * `price * REPEAT_GROWTH ** bought`, so the tenth splice is a project.
   */
  repeatable?: boolean;
}

/** Price growth per purchase of a repeatable shop tile. */
export const REPEAT_GROWTH = 1.6;

/**
 * Tools are priced as a ramp the player climbs **each era**, not as a
 * lifetime purchase (see docs/fossil-eras-plan.md: the shard shop resets at
 * an extinction). They buy no eggs of their own — only more shards — so a
 * long chain would spend most of an era's meteor income on being better at
 * meteors. 5/15/40/100 leaves roughly twice as much for splices, which are
 * what actually moves production.
 */
const tools: ShopItemDef[] = [
  {
    id: 'tool:stone-hammer',
    name: 'Stone Hammer',
    description: 'Each click does 2 damage to a meteor (was 1).',
    price: 5,
    effect: { kind: 'damage', damage: 2 },
  },
  {
    id: 'tool:iron-pick',
    name: 'Iron Pick',
    description: 'Each click does 4 damage to a meteor.',
    price: 15,
    effect: { kind: 'damage', damage: 4 },
    requires: 'tool:stone-hammer',
  },
  {
    id: 'tool:steel-pick',
    name: 'Steel Pick',
    description: 'Each click does 7 damage to a meteor.',
    price: 40,
    effect: { kind: 'damage', damage: 7 },
    requires: 'tool:iron-pick',
  },
  {
    id: 'tool:diamond-drill',
    name: 'Diamond Drill',
    description: 'Each click does 12 damage to a meteor.',
    price: 100,
    effect: { kind: 'damage', damage: 12 },
    requires: 'tool:steel-pick',
  },
];

const nights: ShopItemDef[] = [
  {
    id: 'night:night-shift',
    name: 'Night Shift',
    description: 'Away rate +10%: your dinosaurs lay more while the game is closed.',
    price: 15,
    effect: { kind: 'offlineRate', add: 0.1 },
  },
  {
    id: 'night:long-night',
    name: 'Long Night',
    description: 'Away rate +10%: your dinosaurs lay more while the game is closed.',
    price: 60,
    effect: { kind: 'offlineRate', add: 0.1 },
    requires: 'night:night-shift',
  },
  {
    id: 'night:endless-night',
    name: 'Endless Night',
    description: 'Away rate +10%: your dinosaurs lay more while the game is closed.',
    price: 200,
    effect: { kind: 'offlineRate', add: 0.1 },
    requires: 'night:long-night',
  },
];

/** Gene splices: bought into the inventory, used on a dinosaur of your choosing. */
const splices: ShopItemDef[] = [
  {
    id: 'buy:splice-gene',
    name: 'Gene Splice',
    description: 'Goes to your inventory. Evolves one of the first eight dinosaurs.',
    price: 60,
    effect: { kind: 'grantItem', itemId: 'splice:gene' },
    // Deliberately ungated: a player who would rather grind 60 shards for
    // their first evolution than spend them on tools may do exactly that.
    repeatable: true,
  },
  {
    id: 'buy:splice-prime',
    name: 'Prime Splice',
    description: 'Goes to your inventory. Evolves any dinosaur, however large.',
    price: 900,
    effect: { kind: 'grantItem', itemId: 'splice:prime' },
    requires: 'tool:steel-pick',
    repeatable: true,
  },
];

/**
 * The sky: what falls, rather than what the player swings at it. The last
 * thing the shop sells, behind the whole tool chain.
 */
const skies: ShopItemDef[] = [
  {
    // Cheap enough to buy with an era left to spend it in: at 400 it could
    // only be afforded near the end of one, when the doubling had no time to
    // pay for itself.
    id: 'sky:gravity-well',
    name: 'Gravity Well',
    description: 'Meteors fall twice as often.',
    price: 150,
    effect: { kind: 'meteorRate', mult: 2 },
    requires: 'tool:diamond-drill',
  },
];

export const SHOP_ITEMS: readonly ShopItemDef[] = [...tools, ...nights, ...splices, ...skies];

export const SHOP_ITEMS_BY_ID: ReadonlyMap<string, ShopItemDef> = new Map(
  SHOP_ITEMS.map((i) => [i.id, i]),
);
